import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import io from 'socket.io-client';
import { useAuthContext } from './AuthContext';
import { useErrorMessage } from '../utils/errorMessage';

const SocketContext = createContext({});

/**
 * @description Socket context provider
 * @param {Object} props Props
 * @param {React.ReactNode} props.children Children
 * @returns {React.ReactNode} Socket context provider
 */
export const SocketContextProvider = ({ children }) => {
  const { token, dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();

  const [socket, setSocket] = useState();
  const [connectedUsers, setConnectedUsers] = useState([{}]);
  const [profileLock, setProfileLock] = useState([]);
  const [editSupervisionLock, setEditSupervisionLock] = useState([]);

  useEffect(() => {
    if (!token) return undefined;
    const newSocket = io(`${process.env.REACT_APP_API_URL}?token=${token}`);
    setSocket(newSocket);
    // The socket is closed by the same effect that opened it: on a token
    // refresh, a logout or an unmount. The server handles a disconnection
    // exactly like the former 'logout' event.
    return () => newSocket.close();
  }, [token]);

  useEffect(() => {
    if (socket) {
      const handleConnectedUsers = (users) => setConnectedUsers(users);
      const handleForceDisconnect = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        globalThis.location.reload();
      };

      socket.on('connectedUsers', handleConnectedUsers);
      socket.on('forceDisconnect', handleForceDisconnect);

      return () => {
        socket.off('connectedUsers', handleConnectedUsers);
        socket.off('forceDisconnect', handleForceDisconnect);
      };
    }
    return undefined;
  }, [socket]);

  const onProfileLock = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/editing-rights`
      });

      const newProfileLock = [];
      const newEditSupervisionLock = [];

      data.forEach((item) => {
        if (item.action === 'editing_profile') {
          newProfileLock.push(item);
        }
        if (item.action === 'editing_supervision') {
          newEditSupervisionLock.push(item);
        }
      });

      setProfileLock(newProfileLock);
      setEditSupervisionLock(newEditSupervisionLock);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    if (socket) {
      const handleProfileLock = () => onProfileLock();
      const handleProfileUnlock = () => onProfileLock();
      const handleEditSupervisionLock = () => onProfileLock();
      const handleEditSupervisionUnlock = () => onProfileLock();

      socket.on('profileLock', handleProfileLock);
      socket.on('profileUnlock', handleProfileUnlock);
      socket.on('editSupervisionLock', handleEditSupervisionLock);
      socket.on('editSupervisionUnlock', handleEditSupervisionUnlock);

      return () => {
        socket.off('profileLock', handleProfileLock);
        socket.off('profileUnlock', handleProfileUnlock);
        socket.off('editSupervisionLock', handleEditSupervisionLock);
        socket.off('editSupervisionUnlock', handleEditSupervisionUnlock);
      };
    }
    return undefined;
  }, [socket]);

  const contextValue = useMemo(
    () => ({
      socket,
      connectedUsers,
      onProfileLock,
      profileLock,
      editSupervisionLock
    }),
    [socket, connectedUsers, onProfileLock, profileLock, editSupervisionLock]
  );

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (context === undefined)
    throw new Error('Context must be used within a context provider');
  return context;
};
